import { reasonCopyKey } from 'components/Intelligence/reasonKey';
import i18n from 'utils/i18n';

import { TCustomerInsights } from '../i18n/TModules';
import { wireProductFamiliesFor } from '../registry/productReadiness';
import { CardMetric, MetricTone } from '../types';
import { MetricConfidence } from './confidence';
import { asPercentage } from './metricResult';
import { holdingStatusFromReadinessRow, ProductReadinessRow } from './productReadinessResult';

/**
 * The `*-PR-*` schema as the card's four tiles.
 *
 * `productReadinessResult.ts` parses the wire; this decides what of it a `CardMetric` carries. They
 * are kept apart because the parse is the contract's and this is the screen's: the day the catalogue
 * states a propensity scale, or a band vocabulary, only this file moves.
 *
 * The whole reason a separate builder exists rather than `toCardMetric`: this schema has NO
 * `metric_value` and NO `calculation_status`. Read through the standard row it manufactures an empty
 * status for every Product Readiness metric, which `assembleCard` then reports as the card's own
 * `insufficientData` reason -- degrading the card on the strength of a column the question never
 * declared. Contract PLA-8825, PLA-8828.
 *
 * The grain is AGGREGATED, not per customer: one row is one cell of
 * `product_offer_id x readiness_branch x target_product_holding_status x eligibility_decision x
 * propensity_band` carrying `n_customers`. So every reading below is a SUM of `n_customers` over the
 * cells that answer the tile's question, and a cell count is never a customer count. PLA-8815.
 */

/**
 * The row a single-row consumer reads, out of however many the question returned.
 *
 * Correct only for the per-customer `C-*-PR-*` generation, which is what still calls it
 * (`customer360/productReadiness/readiness.ts`): there a response describes ONE customer and one
 * product. It is deliberately NOT how the aggregated `O-*-PR-*` tiles read their value — `rows[0]`
 * there is one arbitrary cell out of 434 — which is what `aggregateReadiness` below exists for.
 */
export const primaryReadinessRow = (
  rows: ProductReadinessRow[] | undefined
): ProductReadinessRow | undefined => rows?.[0];

/**
 * The rows that belong to the selected product.
 *
 * One `O-*-PR-*` question answers with EVERY product's cells: all nine `O-FIN-PR-*` return the same
 * 434-row table spanning nine offers, differing only in `confidence_score`. So the registry's "one
 * metric per product" join does not narrow the payload and the card has to.
 *
 * Matched on `product_family`, whose slugs the catalogue can write down, rather than on
 * `product_offer_id`, which is a per-tenant hash. A payload this side cannot attribute answers with
 * NO rows rather than with all of them: summing nine products under one product's label is a wrong
 * reading, and an empty tile is not.
 */
export const readinessRowsForProduct = (
  rows: ProductReadinessRow[],
  productId?: string
): ProductReadinessRow[] => {
  if (productId === undefined) {
    return rows;
  }

  const families = wireProductFamiliesFor(productId);

  if (families) {
    return rows.filter(row => families.indexOf(row.product_family ?? '') !== -1);
  }

  // No declared families. A response carrying one offer or none needs no narrowing -- the `C-*`
  // generation is one customer and one product -- and anything wider cannot be attributed.
  const offers = new Set(rows.map(row => row.product_offer_id));

  return offers.size > 1 ? [] : rows;
};

/**
 * The `eligibility_decision` words that answer "yes".
 *
 * `conditionally_eligible` counts: the governed formula names the reading "COUNT(DISTINCT
 * product-applicable eligible customers)" and the vocabulary's other four answers -- `ineligible`,
 * `excluded`, `insufficient_data` and an unstated decision -- are each a "no" or a "not asked".
 * Reading only the bare `eligible` would drop 191 of the 506 customers this tile reports on
 * `bill_pay`, so which of the two the FR means is a product question, raised rather than silently
 * decided twice. PLA-8815.
 */
const ELIGIBLE_DECISIONS = new Set(['eligible', 'conditionally_eligible']);

const isEligible = (row: ProductReadinessRow): boolean =>
  typeof row.eligibility_decision === 'string' &&
  ELIGIBLE_DECISIONS.has(row.eligibility_decision.trim().toLowerCase());

/**
 * A cell's customer count, or `undefined`.
 *
 * `undefined` and never `0`: a cell that stated no count is not a cell that counted nobody, and the
 * difference decides whether a tile reads a figure or stays empty (FR § Data states).
 */
const customersIn = (row: ProductReadinessRow): number | undefined =>
  typeof row.n_customers === 'number' && Number.isFinite(row.n_customers)
    ? row.n_customers
    : undefined;

/**
 * The customers a set of cells covers, or `null` when not one of them stated a count.
 *
 * `null` rather than `0` for the same reason `customersIn` is undefined: the empty state and a
 * measured zero are different facts, and a `0` here is what would put "0 customers" on a card whose
 * response simply carries no `n_customers` column.
 */
const sumCustomers = (rows: ProductReadinessRow[]): number | null =>
  rows.reduce<number | null>((total, row) => {
    const customers = customersIn(row);

    return customers === undefined ? total : (total ?? 0) + customers;
  }, null);

/** One propensity band and the customers in it, summed across the cells that state it. */
export interface ReadinessBandCount {
  /** The wire's own band word, verbatim -- `high`, `medium`, `low`, `insufficient`. */
  band: string;
  customers: number;
}

export interface ReadinessTotals {
  /** Every customer the cells cover, whatever the pipeline decided about them. */
  evaluated: number | null;
  /** Those whose `eligibility_decision` answers yes -- the governed formula's "Eligible Base". */
  eligible: number | null;
  /**
   * The propensity split, weighted by `n_customers`.
   *
   * Cells whose `propensity_band` is null are LEFT OUT rather than gathered under a band this side
   * invents: "not scored" is a real category of the cross-tab and it carries most of the population,
   * so the ring divides the SCORED customers and its shares say so. `readiness_band` is the column
   * the card is named after and it is null on all 434 captured rows, which is why this is a
   * propensity distribution and must be labelled as one. PLA-8815.
   */
  bands: ReadinessBandCount[];
}

const totalsOf = (rows: ProductReadinessRow[], byState: boolean): ReadinessTotals => {
  const byBand = new Map<string, number>();

  rows.forEach(row => {
    const band = (byState ? row.readiness_state : row.propensity_band)?.trim();
    const customers = customersIn(row);

    if (band && customers !== undefined) {
      byBand.set(band, (byBand.get(band) ?? 0) + customers);
    }
  });

  return {
    evaluated: sumCustomers(rows),
    eligible: sumCustomers(rows.filter(isEligible)),
    bands: Array.from(byBand, ([band, customers]) => ({ band, customers }))
  };
};

/**
 * One product's cells, summed whole and per side of the target-product holding split.
 *
 * A row whose holding the response did not state falls in `overall` and in NEITHER branch, so the
 * two branches never borrow a customer nobody placed.
 */
export interface ReadinessAggregate {
  overall: ReadinessTotals;
  held: ReadinessTotals;
  notHeld: ReadinessTotals;
}

/* The ring splits by the governed `readiness_state` once any row states something beyond
   `unavailable` (`O-BFS-PR-*`, 2026-10-08). A response that is `unavailable` throughout (`O-FIN-PR-*`)
   keeps the propensity split, the only one that divides its population. PLA-8828. */
const splitsByState = (rows: ProductReadinessRow[]): boolean =>
  rows.some(row => {
    const state = row.readiness_state?.trim().toLowerCase();

    return Boolean(state) && state !== 'unavailable';
  });

export const aggregateReadiness = (rows: ProductReadinessRow[]): ReadinessAggregate => {
  const byState = splitsByState(rows);

  return {
    overall: totalsOf(rows, byState),
    held: totalsOf(
      rows.filter(row => holdingStatusFromReadinessRow(row) === 'held'),
      byState
    ),
    notHeld: totalsOf(
      rows.filter(row => holdingStatusFromReadinessRow(row) === 'not_held'),
      byState
    )
  };
};

/** A rate over two sums, or `null` where either side is missing or the base is empty. */
export const readinessRate = (
  numerator: number | null,
  denominator: number | null
): number | null =>
  numerator === null || denominator === null || denominator === 0
    ? null
    : asPercentage(numerator, denominator);

/**
 * What each of the card's four tiles reads off the aggregate.
 *
 * The keys are `registry/metricIdentifiers.ts`'s own, so a tile renamed there stops resolving here
 * rather than silently reading a neighbour's figure. `product_gap_customers` is the NON-HOLDER
 * population: the gap a product has to close is the customers who do not hold it yet, which is the
 * split the governed formula names. PLA-8815.
 */
export const readinessTileValue = (
  key: string,
  totals: ReadinessAggregate
): number | null | undefined => {
  switch (key) {
    case 'eligible_customers':
      return totals.overall.eligible;
    case 'eligible_customer_rate':
      return readinessRate(totals.overall.eligible, totals.overall.evaluated);
    case 'product_gap_customers':
      return totals.notHeld.evaluated;
    case 'total_evaluated':
      return totals.overall.evaluated;
    default:
      // A tile this file has no rule for. `undefined`, not `null`, so the caller can tell "no rule"
      // from "the rule ran and nothing was measured".
      return undefined;
  }
};

/**
 * This card's confidence, read from the five governed columns the landed schema declares.
 *
 * It used to read `composite_confidence`, a column the `O-*` generation does not carry at all, so
 * every badge was blank over a live 0.9205. `composite_confidence` stays as the fallback because the
 * `C-*` generation still names it. The first cell that states a score wins: the block is per PRODUCT
 * on this schema and identical across a product's cells (verified on all nine fintech offers).
 */
const confidenceFromReadinessRows = (rows: ProductReadinessRow[]): MetricConfidence | undefined => {
  const row =
    rows.find(candidate => typeof candidate.confidence_score === 'number') ??
    rows.find(candidate => typeof candidate.composite_confidence === 'number') ??
    rows[0];

  if (!row) {
    return undefined;
  }

  const confidence: MetricConfidence = {
    score:
      typeof row.confidence_score === 'number' ? row.confidence_score : row.composite_confidence,
    band: row.confidence_band ?? null,
    limitingReason: row.confidence_limiting_reason ?? null,
    capApplied: row.confidence_cap_applied ?? null,
    handoffOk: row.handoff_confidence_ok
  };

  // A row of nulls draws no badge at all rather than an empty one.
  return Object.values(confidence).some(field => field !== null && field !== undefined)
    ? confidence
    : undefined;
};

/**
 * The wire's own "this product has no readiness model here", across the product's whole cell set.
 *
 * Read over EVERY cell rather than off one: `readiness_model_status` is per cell (386 configured
 * against 48 not, over the nine fintech offers), so a single not-configured cell beside 40
 * configured ones is a gap in a model that exists, not an absent model.
 */
const modelNotConfigured = (rows: ProductReadinessRow[]): boolean =>
  rows.length > 0 &&
  rows.every(
    row => row.readiness_not_configured === true || row.readiness_state === 'not_configured'
  );

/**
 * The per-customer reading, for a tile whose key names no aggregate.
 *
 * `propensity_score` is the column: `type/Decimal` on the 25-column `C-*-PR-*` schema
 * (`productReadinessResult.ts`), one row per customer per product, and the reading this builder
 * carried before the aggregated `O-*` generation landed. The `O-*` generation DROPS the column
 * altogether, so this can never answer for an aggregate tile even if one reached it.
 *
 * Kept apart from `readinessTileValue`, which is the `O-*` card's four-tile rule over summed
 * `n_customers`. That rule has nothing to say about a tile keyed for a product
 * (`wallet_activation_reactivation_readiness`), returns `undefined` for it, and until this existed
 * every one of the 36 `C-*-PR-*` product tiles was emitted with `value: null` -- which
 * `ProductReadinessSection`'s `hasProductReading` then read as "no reading", so no product body
 * drew. PLA-8828.
 */
const customerReadinessValue = (rows: ProductReadinessRow[]): number | null => {
  const row = primaryReadinessRow(rows);

  // `readiness_score` first, and only where the wire actually sent a number: it is THE readiness
  // figure on the 64-column schema, 0-100 and banded at 75/50, while `propensity_score` is a 0-1
  // probability the catalogue bands nowhere near those cuts. Falling through on a null rather than
  // on `??` alone keeps the pre-existing reading for the nine `C-BFS-PR-*` ids that declare the
  // column and answer with no rows at all. PLA-8828.
  if (typeof row?.readiness_score === 'number') {
    return row.readiness_score;
  }

  // A row that states a governed `readiness_state` with no score is a state, not a propensity
  // reading: falling back to `propensity_score` would print a 0-1 probability as its score.
  return row?.readiness_state ? null : row?.propensity_score ?? null;
};

/* The wire's governed `readiness_state` vocabulary, as the band keys the copy and tones are keyed
   by. `insufficient` is the wire's spelling of the card's `insufficient_data`. */
const READINESS_STATE_BANDS: Record<string, string> = {
  ready: 'ready',
  potential: 'potential',
  not_ready: 'not_ready',
  suppressed: 'suppressed',
  insufficient: 'insufficient_data',
  insufficient_data: 'insufficient_data',
  unavailable: 'unavailable'
};

const UNSCORED_BANDS = new Set(['suppressed', 'insufficient_data', 'unavailable']);

const governedBandOf = (row: ProductReadinessRow | undefined): string | undefined => {
  const state = row?.readiness_state?.trim().toLowerCase();

  return state && Object.prototype.hasOwnProperty.call(READINESS_STATE_BANDS, state)
    ? READINESS_STATE_BANDS[state]
    : undefined;
};

const BANDS_GROUP = `${TCustomerInsights}.ProductReadiness.Bands`;

/**
 * The five governed readiness categories, and the tone each is drawn in.
 *
 * Keyed by the wire's own lower-case word so an unrecognised band falls out of the map and draws no
 * pill -- the same "a code with no copy renders nothing" rule `components/Intelligence/reasonKey.ts`
 * states for every other governed vocabulary on this screen.
 */
const BAND_TONES: Record<string, MetricTone> = {
  ready: 'positive',
  potential: 'warning',
  not_ready: 'negative',
  suppressed: 'negative',
  insufficient_data: 'info',
  unavailable: 'info'
};

/** The governed confidence gate, below which a score is Insufficient Data rather than a band. */
const MIN_RESULT_CONFIDENCE = 0.6;

/**
 * The band a `readiness_score` falls in: Ready `>= 75`, Potential 50-74, Not Ready `< 50`, each
 * conditional on a result confidence of at least 0.60 (catalogue § Final distribution within each
 * branch; `gov-final/08-product-readiness.md`, `09-confidence.md`).
 *
 * A confidence the response did NOT state leaves the score banded on its own rather than demoted:
 * "confidence below 0.60" and "no confidence column" are different facts, and reading the second as
 * the first would put Insufficient Data on every row of a generation that declares no confidence.
 */
const bandFromScore = (score: number, confidence: number | null | undefined): string => {
  if (typeof confidence === 'number' && confidence < MIN_RESULT_CONFIDENCE) {
    return 'insufficient_data';
  }

  if (score >= 75) {
    return 'ready';
  }

  return score >= 50 ? 'potential' : 'not_ready';
};

/**
 * Which of the five categories this reading is in, as the wire states it or as the catalogue's
 * thresholds put it.
 *
 * Order is the catalogue's own: a stated SUPPRESSION or INSUFFICIENCY reason names the state it
 * belongs to, so it outranks both the band column and the score -- a suppressed customer is
 * suppressed whatever figure was computed before the guardrail fired. `readiness_band` next, because
 * the server's band is authoritative wherever it sent one (PLA-8825 § G32), and the thresholds are
 * this side's reconstruction of a rule the server already ran.
 */
const readinessBandOf = (
  row: ProductReadinessRow | undefined,
  confidence: number | null | undefined
): string | undefined => {
  if (!row) {
    return undefined;
  }

  // The governed state outranks the reason columns: live `unavailable` rows carry their reason in
  // `readiness_insufficiency_reason`, which read first would relabel them Insufficient Data.
  const state = governedBandOf(row);

  if (state) {
    return state;
  }

  if (row.readiness_suppression_reason) {
    return 'suppressed';
  }

  if (row.readiness_insufficiency_reason) {
    return 'insufficient_data';
  }

  const stated = row.readiness_band?.trim().toLowerCase();

  if (stated) {
    return stated;
  }

  return typeof row.readiness_score === 'number'
    ? bandFromScore(row.readiness_score, confidence)
    : undefined;
};

/** The band as this tile's status pill, or nothing where the band has no copy of its own. */
const readinessBandStatus = (
  row: ProductReadinessRow | undefined,
  confidence: number | null | undefined
): CardMetric['status'] | undefined => {
  const band = readinessBandOf(row, confidence);
  const key = band ? reasonCopyKey(BANDS_GROUP, band) : null;

  return key && band ? { label: i18n.t(key), tone: BAND_TONES[band] ?? 'info' } : undefined;
};

/**
 * The governed reason behind a Suppressed or Insufficient Data reading, as the pair `assembleCard`
 * already turns into the card's data state and `CardDataStateBlock` keys through
 * `DataStateReasons`.
 *
 * Suppression first, for the reason the band order above is in. Both are read only where the
 * response carried the column, and `assembleCard` acts on the pair only when the tile has no value
 * -- so a reading that survived its own guardrail keeps its figure and states the reason beside it
 * rather than being blanked here.
 */
const readinessUnavailability = (
  row: ProductReadinessRow | undefined
): Pick<CardMetric, 'unavailableStatus' | 'unavailableReasonCode'> | undefined => {
  const state = governedBandOf(row);

  if (state && UNSCORED_BANDS.has(state)) {
    const reason = row?.readiness_suppression_reason ?? row?.readiness_insufficiency_reason;

    return { unavailableStatus: state, ...(reason ? { unavailableReasonCode: reason } : {}) };
  }

  if (row?.readiness_suppression_reason) {
    return {
      unavailableStatus: 'suppressed',
      unavailableReasonCode: row.readiness_suppression_reason
    };
  }

  return row?.readiness_insufficiency_reason
    ? {
        unavailableStatus: 'insufficient_data',
        unavailableReasonCode: row.readiness_insufficiency_reason
      }
    : undefined;
};

/**
 * One Product Readiness tile.
 *
 * No `status` pill: it used to name `rows[0]`'s `propensity_band`, which is one arbitrary cell of
 * the cross-tab, and the same word then appeared on all four tiles. The propensity split is drawn
 * once, as the card's ring, over the whole population. PLA-8815.
 */
export const readinessCardMetric = (
  base: Pick<CardMetric, 'key' | 'label' | 'format'>,
  rows: ProductReadinessRow[],
  productId?: string
): CardMetric => {
  const scoped = readinessRowsForProduct(rows, productId);
  const confidence = confidenceFromReadinessRows(scoped);
  const aggregate = readinessTileValue(base.key, aggregateReadiness(scoped));
  // `undefined` is "no aggregate rule for this key", which is every per-product tile; a rule that
  // ran and measured nothing answers `null` and is left alone.
  const value = aggregate === undefined ? customerReadinessValue(scoped) : aggregate;
  // Customer-grain only. The pill the comment below retired was `rows[0].propensity_band` on the
  // AGGREGATED schema, where row zero is one arbitrary cell of a 434-row cross-tab; on the `C-*`
  // generation the customer IS the row, and the band is that customer's own governed category.
  const customerRow = aggregate === undefined ? primaryReadinessRow(scoped) : undefined;
  const status = readinessBandStatus(customerRow, confidence?.score);

  return {
    ...base,
    value: value ?? null,
    ...(confidence ? { confidence } : {}),
    ...(status ? { status } : {}),
    ...readinessUnavailability(customerRow),
    ...(modelNotConfigured(scoped) ? { notConfigured: true } : {}),
    // The product's own cells, not the whole payload: the section builds both rings off this, and
    // handing it nine products' rows is the bug the filter above exists to stop.
    readiness: scoped
  };
};

export default readinessCardMetric;
